/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

export interface UiInlineSelectPillOption<TValue extends string | number = string | number> {
    label: string
    value: TValue
    description?: string
    disabled?: boolean
    disabledReason?: string
}

interface UiInlineSelectPillProps<TValue extends string | number> {
    value: TValue
    options: UiInlineSelectPillOption<TValue>[]
    renderPill: (value: TValue) => React.ReactNode
    onChange: (value: TValue) => void | Promise<void>
    disabled?: boolean
    ariaLabel?: string
    className?: string
}

const POPOVER_WIDTH = 220
const POPOVER_MARGIN = 4

function UiInlineSelectPill<TValue extends string | number>({
    value,
    options,
    renderPill,
    onChange,
    disabled,
    ariaLabel,
    className,
}: UiInlineSelectPillProps<TValue>) {
    const [open, setOpen] = React.useState(false)
    const [busyValue, setBusyValue] = React.useState<TValue | null>(null)
    const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null)
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const popoverRef = React.useRef<HTMLDivElement>(null)

    const close = React.useCallback(() => {
        setOpen(false)
        setPosition(null)
    }, [])

    const computePosition = React.useCallback(() => {
        const button = buttonRef.current
        if (!button) {
            return
        }
        const rect = button.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const popoverEstimatedHeight = Math.min(320, options.length * 44 + 16)
        const spaceBelow = viewportHeight - rect.bottom
        const top =
            spaceBelow >= popoverEstimatedHeight + POPOVER_MARGIN
                ? rect.bottom + POPOVER_MARGIN
                : Math.max(8, rect.top - popoverEstimatedHeight - POPOVER_MARGIN)
        const maxLeft = window.innerWidth - POPOVER_WIDTH - 8
        const left = Math.min(Math.max(8, rect.left), maxLeft)
        setPosition({ top, left })
    }, [options.length])

    React.useEffect(() => {
        if (!open) {
            return
        }
        computePosition()

        const handleScroll = () => computePosition()
        const handleResize = () => computePosition()
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null
            if (
                popoverRef.current?.contains(target) ||
                buttonRef.current?.contains(target)
            ) {
                return
            }
            close()
        }
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
            }
        }

        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleResize)
        window.addEventListener('pointerdown', handlePointerDown, true)
        window.addEventListener('keydown', handleKey)

        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('pointerdown', handlePointerDown, true)
            window.removeEventListener('keydown', handleKey)
        }
    }, [open, computePosition, close])

    const stopRow = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    const handleSelect = async (nextValue: TValue, optionDisabled?: boolean) => {
        if (optionDisabled || nextValue === value) {
            close()
            return
        }
        try {
            setBusyValue(nextValue)
            await onChange(nextValue)
        } finally {
            setBusyValue(null)
            close()
        }
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                data-row-selection-toggle="true"
                onMouseDown={stopRow}
                onClick={(event) => {
                    stopRow(event)
                    if (disabled) {
                        return
                    }
                    setOpen((prev) => !prev)
                }}
                className={clsx(
                    'UiInlineSelectPill inline-flex items-center cursor-pointer rounded-full hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-cta-light',
                    disabled && 'cursor-not-allowed opacity-60',
                    className
                )}
            >
                {renderPill(value)}
            </button>
            {open && position
                ? createPortal(
                      <div
                          ref={popoverRef}
                          role="listbox"
                          data-row-selection-toggle="true"
                          onMouseDown={stopRow}
                          onClick={stopRow}
                          style={{
                              position: 'fixed',
                              top: position.top,
                              left: position.left,
                              width: POPOVER_WIDTH,
                              zIndex: 1000,
                          }}
                          className="UiInlineSelectPillPopover bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 max-h-80 overflow-y-auto"
                      >
                          {options.map((option) => {
                              const isActive = option.value === value
                              const isBusy = option.value === busyValue
                              const isOptionDisabled = Boolean(option.disabled)
                              return (
                                  <button
                                      key={String(option.value)}
                                      type="button"
                                      disabled={isBusy || isOptionDisabled}
                                      title={isOptionDisabled ? option.disabledReason : undefined}
                                      onClick={(event) => {
                                          stopRow(event)
                                          void handleSelect(option.value, isOptionDisabled)
                                      }}
                                      className={clsx(
                                          'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                                          isActive
                                              ? 'bg-cyan-50 text-cta-light dark:bg-cyan-900/30 dark:text-cta-light font-semibold'
                                              : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800',
                                          (isBusy || isOptionDisabled) && 'opacity-50',
                                          isBusy && 'cursor-wait',
                                          isOptionDisabled && 'cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent'
                                      )}
                                  >
                                      <span className="flex-1 truncate">{option.label}</span>
                                      {isActive && (
                                          <span className="text-cta-light" aria-hidden>
                                              ✓
                                          </span>
                                      )}
                                  </button>
                              )
                          })}
                      </div>,
                      document.body
                  )
                : null}
        </>
    )
}

export default UiInlineSelectPill
